import React, { useState, useEffect, useMemo, useRef } from 'react';
import { components } from 'react-select';
import { HiArrowLeft, HiCheck, HiX } from 'react-icons/hi';
import { Spinner, Tooltip } from 'flowbite-react';
import { MultiSelect, SingleSelect } from "../../components/common/Search";
import { StrategyEntityItemType, StrategyMapListItem, useStrategyMapService } from "../../services/strategyMap";
import TooltipWrapper from '../../shared/atoms/TooltipWrapper';

// Custom React Tooltip Component (Extracted from DependencyForm.tsx)
interface CustomTooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}



interface ProjectOption {
  value: string;
  label: string;
  project_start_date?: string;
  project_end_date?: string;
  task_start_date?: string;
  task_end_date?: string;
  adj_start_date?: string;
  adj_end_date?: string;
  duration?: number;
  isDisabled?: boolean;
  type?: string;
  error?: string;
}

interface StrategyMapDependencyItem {
  id: string;
  type: string;
  name: string;
  entity_type: string;
}

export interface StrategyMapDependency {
  id: string;
  type: string;
  name: string;
  dependency: StrategyMapDependencyItem[];
  _uid?: string;
}

interface StrategyMapDependencyMapperProps {
  onSave?: (data: StrategyMapDependency) => void;
  onUpdate?: (data: StrategyMapDependency) => void;
  onCancel?: () => void;
  data: StrategyEntityItemType[];
  defaultData?:null | {
    selectedSource: string;
    selectedDependencies: string[];
  }
}

export function StrategyMapDependencyMapper({
  onSave,
  onUpdate,
  onCancel,
  data,
  defaultData,
}: StrategyMapDependencyMapperProps) {
  const [entities, setEntities] = useState<StrategyEntityItemType[]>(data);

  // Local state for the current dependency being built
  const [selectedSource, setSelectedSource] = useState<string>(
    defaultData?.selectedSource || "",
  );
  const [selectedDependencies, setSelectedDependencies] = useState<string[]>(
    defaultData?.selectedDependencies || [],
  );

  const projectOptions: ProjectOption[] = useMemo(() => {
    return entities.map((item) => ({
      value: item.id,
      type: item?.type ?? "",
      label: item.name,
      project_start_date: item.start_date,
      project_end_date: item.end_date,
    }));
  }, [entities]);

  useEffect(() => {
    setEntities(data);
  }, [data]);

  useEffect(() => {
    if (defaultData) {
      setSelectedSource(defaultData.selectedSource);
      setSelectedDependencies(defaultData.selectedDependencies);
    }
  }, [defaultData]);

  const validateDependencyCondition = (
    dependentProject: ProjectOption,
    dependencyProject: ProjectOption,
  ): { isValid: boolean; error?: string } => {
    if (!dependentProject || !dependencyProject) {
      return { isValid: false, error: "Project data not available" };
    }

    const dependentStartStr = dependentProject.task_start_date || dependentProject.project_start_date || dependentProject.adj_start_date;
    const dependentEndStr = dependentProject.task_end_date || dependentProject.project_end_date || dependentProject.adj_end_date;
    const dependencyStartStr = dependencyProject.task_start_date || dependencyProject.project_start_date || dependencyProject.adj_start_date;
    const dependencyEndStr = dependencyProject.task_end_date || dependencyProject.project_end_date || dependencyProject.adj_end_date;

    if (
      !dependentStartStr ||
      !dependentEndStr ||
      !dependencyStartStr ||
      !dependencyEndStr
    ) {
      return { isValid: false, error: "Missing date information" };
    }

    const dependentStart = new Date(dependentStartStr);
    const dependentEnd = new Date(dependentEndStr);
    const dependencyStart = new Date(dependencyStartStr);
    const dependencyEnd = new Date(dependencyEndStr);

    // Backend condition 2: The dependent project cannot start before the project it depends on
    if (dependencyStart > dependentStart) {
      return {
        isValid: false,
        error: `Cannot depend on this project. The dependent project must start on or after the project it depends on.`,
      };
    }

    // Backend condition 3: The dependent project cannot end before the project it depends on
    if (dependencyEnd > dependentEnd) {
      return {
        isValid: false,
        error: `Cannot depend on this project. The dependent project must end on or after the project it depends on.`,
      };
    }

    return { isValid: true };
  };

  const processedDependencyOptions = useMemo(() => {
    if (!selectedSource) return projectOptions;

    const selectedProjectData = projectOptions.find(
      (p) => p.value === selectedSource,
    );
    if (!selectedProjectData) return projectOptions;

    return projectOptions.map((option) => {
      if (option.value === selectedSource) {
        return {
          ...option,
          isDisabled: true,
          error: "An item cannot depend on itself",
        };
      }

      const validation = validateDependencyCondition(
        selectedProjectData,
        option,
      );

      if (!validation.isValid) {
        return {
          ...option,
          isDisabled: true,
          error: validation.error,
        };
      }

      return option;
    });
  }, [selectedSource, projectOptions]);

  const hasValidationErrors = useMemo(() => {
    if (!selectedSource || selectedDependencies.length === 0) return false;

    return selectedDependencies.some((depId) => {
      const option = processedDependencyOptions.find(
        (opt) => opt.value === depId,
      );
      return option?.isDisabled;
    });
  }, [selectedSource, selectedDependencies, processedDependencyOptions]);

  const handleDependencyChange = (values: any[]) => {
    const validValues = values.filter((v) => {
      const option = processedDependencyOptions.find(
        (opt) => opt.value === v.value,
      );
      return !option?.isDisabled;
    });
    setSelectedDependencies(validValues.map((v: any) => v.value));
  };

  const handleSave = () => {
    if (
      !selectedSource ||
      selectedDependencies.length === 0 ||
      hasValidationErrors
    )
      return;

    const sourceProject = projectOptions.find(
      (p) => p.value === selectedSource,
    );
    const dependencyItems = selectedDependencies.map((depId) => {
      const project = projectOptions.find((p) => p.value === depId);
      return {
        id: depId,
        type: project?.type || "project",
        name: project?.label || "",
      };
    });


    if (defaultData) {
      onUpdate?.({
        id: selectedSource,
        type: sourceProject?.type || "roadmap",
        name: sourceProject?.label ?? "NA",
        dependency: dependencyItems,
      });
    } else {
      onSave?.({
        id: selectedSource,
        type: sourceProject?.type || "roadmap",
        name: sourceProject?.label ?? "NA",
        dependency: dependencyItems,
      });
    }

    // Clear form after save
    setSelectedSource("");
    setSelectedDependencies([]);
  };

  const handleClear = () => {
    setSelectedSource("");
    setSelectedDependencies([]);
    onCancel?.();
  };

  const singularEntityType = "Work Item";

  return (
    <div className="w-full p-4 rounded-xl bg-light-surfacePrimary dark:bg-secondary-dark border border-light-border-default dark:border-slate-700 animate-fade-in mx-auto mt-2">
      {
        <>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-base font-bold text-light-text-primary dark:text-white">
              {defaultData ? "Edit Dependency" : "Add Dependency"}
            </h2>
          </div>

          <div className="mb-3">
            <label className="block text-sm font-semibold mb-2 text-light-text-primary dark:text-gray-200">
              {singularEntityType}
            </label>
            <SingleSelect
              name="select-item"
              options={projectOptions}
              placeholder="Select..."
              onChange={(value: any) => {
                setSelectedSource(value?.value || "");
                setSelectedDependencies([]);
              }}
              defaultValue={projectOptions.find(
                (p) => p.value === selectedSource,
              )}
              width="w-full"
            />
          </div>

          <div className="my-3 border-t border-light-border-default dark:border-slate-700" />

          <div className="mb-3">
            <label className="block text-sm font-semibold mb-2 text-light-text-primary dark:text-gray-200">
              Depends On
            </label>
            <MultiSelect
              name="select-dependency"
              options={processedDependencyOptions}
              value={processedDependencyOptions.filter(
                (p) =>
                  selectedDependencies.includes(p.value) &&
                  !p.isDisabled              )}
              onChange={handleDependencyChange}
              placeholder="Select..."
              isClearable={false}
              width="w-full"
              components={{
                Option: (props: any) => {
                  const { data, innerRef, innerProps } = props;

                  const formatDate = (dateStr: string) => {
                    if (!dateStr) return "N/A";
                    return new Date(dateStr).toLocaleDateString();
                  };

                  if (data.isDisabled) {
                    return (
                      <TooltipWrapper
                        message={
                          <div className="text-xs ">
                            <div className="font-semibold mb-2 text-red-800 dark:text-red-600">
                              {data.error}
                            </div>
                            {data.project_start_date && (
                              <div className="space-y-1 text-gray-800 dark:text-gray-200">
                                <div>
                                  <span className="font-medium">Start:</span>{" "}
                                  {formatDate(data.project_start_date)}
                                </div>
                                {data.project_end_date && (
                                  <div>
                                    <span className="font-medium">End:</span>{" "}
                                    {formatDate(data.project_end_date)}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        }
                      >
                        <div
                          ref={innerRef}
                          {...innerProps}
                          className="cursor-not-allowed px-3 py-2 flex items-center justify-between bg-gray-50 dark:bg-gray-800"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center gap-2">
                            <span className="opacity-50 text-gray-800 dark:text-gray-400 text-sm font-medium">
                              {data.label}
                            </span>
                            <span className="text-xs text-red-400 dark:text-red-600 font-medium">
                              ⚠
                            </span>
                          </div>
                        </div>
                      </TooltipWrapper>
                    );
                  }

                  return <components.Option {...props} />;
                },
              }}
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              className="px-3 py-1.5 rounded-lg bg-light-surfaceSecondary text-light-text-primary font-semibold hover:bg-light-interactive-hover dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600 text-sm flex items-center gap-1 border border-light-border-default dark:border-slate-600 transition-colors duration-200"
              onClick={handleClear}
            >
              <HiX size={16} /> Clear
            </button>
            {(() => {
              const isDisabled = !selectedSource || selectedDependencies.length === 0 || hasValidationErrors;
              const tooltipMessage = hasValidationErrors
                ? "Some selected dependencies violate condition"
                : "";
              const saveButton = (
                <button
                  className={`px-3 py-1.5 rounded-lg font-semibold text-sm flex items-center gap-1 transition-colors duration-200 ${
                    isDisabled
                      ? "bg-light-surfaceSecondary text-light-text-muted cursor-not-allowed border border-light-border-default"
                      : "bg-primary-light text-white hover:bg-primary-hover shadow-sm"
                  }`}
                  onClick={handleSave}
                  disabled={isDisabled}
                >
                  <HiCheck size={16} />{" "}
                  {defaultData ? "Update Dependency" : "Add Dependency"}
                </button>
              );
              // Only show tooltip when there are validation errors to avoid blocking the dropdown
              if (tooltipMessage) {
                return (
                  <Tooltip content={tooltipMessage}>
                    {saveButton}
                  </Tooltip>
                );
              }
              return saveButton;
            })()}
          </div>
        </>
      }
    </div>
  );
}